import { useMemo } from 'react'

import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  Link,
  Text,
  fontSizes,
} from '@enechain/ecloud-designsystem'
import { FaChevronRightRegular } from '@enechain/ecloud-icons'

import {
  AwaitingVoltageHatchPattern,
  useAwaitingVoltageHatchId,
} from '~/components/balance/AwaitingVoltageMark'
import { DemandPeakPill } from '~/components/balance/DemandPeak'
import { useDemandTooltipProps } from '~/components/balance/DemandTooltip'
import type {
  DemandTooltipKoma,
  DemandTooltipVisibility,
} from '~/components/balance/DemandTooltip'
import {
  BALANCE_AWAITING_VOLTAGE_HATCH,
  BALANCE_DEMAND_SERIES_COLORS,
  BALANCE_VOLTAGE_BAND_SEPARATOR_COLOR,
  BALANCE_VOLTAGE_CLASS_COLORS,
} from '~/constants/chartPalette'
import { iconSize } from '~/constants/iconSize'
import { KOMA_COUNT } from '~/constants/koma'
import { chartTypography } from '~/constants/typography'
import { convertAreaLabel } from '~/models/area'
import { awaitingKomaRanges } from '~/models/awaitingVoltage'
import { formatChartTick } from '~/models/chartFormatting'
import {
  isTargetDateBeforeTodayInJst,
  isTargetDateTodayInJst,
  toProtoDate,
} from '~/models/dateTime'
import { komaNumberToTime } from '~/models/komaToTime'
import { formatKwh } from '~/models/kwhDisplay'
import { hasVoltageBreakdown, topVoltageClass } from '~/models/voltageBreakdown'
import type { WeatherActualKoma } from '~/models/weather/actualKoma'
import {
  buildPastActualWeatherIcons,
  komaWeatherAt,
} from '~/models/weather/actualKoma'
import {
  buildWeatherStripDisplayIcons,
  weatherStripGutterWidths,
} from '~/models/weather/koma'
import { WORKFLOW_ID_PARAM } from '~/models/workflowIdParam'
import type { SeriesVisibility } from '~/pages/BalanceDemand/constants'
import type { AreaCodePeakSummary } from '~/pages/BalanceDemand/hooks/useDemandDashboard'
import type { KomaData } from '~/pages/BalanceDemand/models/generateMockData'
import { routePaths } from '~/routes/routePaths'

type Props = {
  areaCode: AreaCode
  komaData: KomaData[]
  /** 天気アイコン帯 (48 スロット)。
   *  出典は weather-icon-unify-48koma 設計書 AC-07/09 だが、 **描画位置は #5087 で改訂済み**
   *  (run 中央配置 → 3 時間ごとの固定ピッチ)。 詳細は `buildWeatherStripDisplayIcons` の JSDoc。 */
  weatherIcons: string[]
  /** 予想気温 (気象予報 gRPC 由来、コマ index 0..47。正時の点以外は null)。
   *  ツールチップの「予想気温」に使う (AC-BDB-010b)。 */
  temperatureForecasts: ReadonlyArray<number | null>
  gcKoma: number
  currentKoma: number
  /** 表示中の日付。当日以外は現在時刻縦線・GC 縦線・GC 以降網掛けをいずれも
   *  描画しない (spec AC-BDB-022a。詳細画面 `BalanceDemandDetail/DetailChart`
   *  と同じ扱い)。 */
  displayDate: Date
  seriesVisibility: SeriesVisibility
  /** 需要ピーク (backend 算出)。実績が 1 コマも無い、 または実績のあるコマがすべて低圧未着のエリアは null → ピル行を出さない。 */
  summary: AreaCodePeakSummary | null
  /** 実績気象 48 コマ。ピルバッジの気温・天気アイコンの供給元。 */
  weatherActual: readonly WeatherActualKoma[]
  /** workflow-back-bar-readonly AC-13: 詳細リンクへ引き継ぐ caseId (undefined = 引き継がない)。 */
  caseId?: string
}

// チャート plot 領域の実寸。天気帯のスペーサーと Recharts の YAxis width /
// margin の両方がこれを参照し、アイコン位置と X 軸時刻の整列を保つ。
const Y_AXIS_WIDTH = 84
const CHART_MARGIN_RIGHT = 2
const RIGHT_AXIS_WIDTH = 40

/**
 * 右 Y 軸 (気温・露点) の目盛りだけ他軸より 1px 小さくする。
 *
 * **class は目盛り text 自身に付ける。** recharts 3.x は目盛りラベルを軸の `<g>`
 * から**別の z-index レイヤーへ吊り上げて**描くため、`<YAxis className>` を起点に
 * した子孫 selector (`.chart-axis-right .recharts-text`) は**目盛りに届かない**
 * (実 DOM 実測: `.chart-axis-right` 配下の `.recharts-text` は 0 件。ラベルは
 * `recharts-zIndex-layer_* > .recharts-yAxis-tick-labels` 側にある)。左右の Y 軸は
 * どちらも `recharts-yAxis-tick-labels` なので、その class でも区別できない。
 */
const RIGHT_AXIS_TICK_CLASS = 'chart-axis-right-tick'
const RIGHT_AXIS_TICK_FONT_SIZE = chartTypography.axis.fontSize - 1

/**
 * 「現在」 縦線ラベルの class。
 *
 * design は 現在 = 10px / GC = 11px (`js/charts.js:1272` / `:1276`)。 GC の 11px は
 * コンテナの `.recharts-text` 規則 (軸目盛りと同じ 11px) にそのまま乗るので指定不要だが、
 * **現在の 10px はその規則に負ける**。 `label={{ fontSize: 10 }}` は SVG の presentation
 * 属性になり、 CSS には勝てない (= #7065 で軸目盛りを直したのと同じ罠)。
 *
 * **`recharts-label` を自分で並べているのは必須**。 `label={{ className }}` は
 * recharts が既定で付ける `recharts-label` を**置き換える** (実測: className を
 * 渡すとラベルが `.recharts-label` で引けなくなる)。 素の class 名に依存する
 * 実装なので、 テストで両方が実際に付いていることを固定してある。
 *
 * 上書きは**複合セレクタ** (`.recharts-label.<class>`、 詳細度 0,2,0) で
 * `.recharts-text` (0,1,0) に確実に勝たせる。 単一 class 同士だと宣言順に依存し、
 * 順序を入れ替えた時に黙って壊れる。
 */
const CURRENT_LABEL_CLASS = 'chart-current-label'
const CURRENT_LABEL_CLASS_NAMES = `recharts-label ${CURRENT_LABEL_CLASS}`
const CURRENT_LABEL_FONT_SIZE = chartTypography.axis.fontSize - 1

/**
 * 右 Y 軸 (気温・露点) の目盛り。 recharts 内蔵 tick と違い `.recharts-text` を
 * 持たせず、 専用 class だけを付ける — 同じ要素に両方付くと
 * `.recharts-text` (11px) と競合し、 どちらが勝つかが宣言順に依存するため。
 * 呼び出し元は `<YAxis tick={<RightAxisTick />} />` の element 形で渡す
 * (recharts が x/y/payload を cloneElement で注入する)。
 */
const RightAxisTick = ({
  x,
  y,
  payload,
}: {
  x?: number
  y?: number
  payload?: { value: number }
}): React.ReactElement => (
  <text
    className={RIGHT_AXIS_TICK_CLASS}
    fill={LightModeSemanticColors.Text.midEmphasis.value}
    dy={4}
    textAnchor="start"
    x={x}
    y={y}
  >
    {payload !== undefined ? payload.value : ''}
  </text>
)

export const AreaCodeChart = ({
  areaCode,
  komaData,
  weatherIcons,
  temperatureForecasts,
  gcKoma,
  currentKoma,
  seriesVisibility,
  summary,
  weatherActual,
  caseId,
  displayDate,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  /**
   * GC 線の位置 = **次に締まるコマの頭** (= 網掛けの開始位置)。
   *
   * ただし **全 48 コマが GC 済みのときは 24:00 になってしまう**。X 軸のドメインは
   * 00:00〜23:30 なので、Recharts は範囲外の ReferenceLine を捨て (`ifOverflow:
   * 'discard'` 既定)、**GC 線もラベルも消える** (2026-09-07 レビュー指摘)。
   *
   * design (`js/charts.js:866-874`) は同じ場面を明示的に分けている:
   *
   * > 全コマ GC 済み (48) は「この先」が無いので、従来どおり最終コマの頭に線だけ残す
   *
   * つまり 48 のときは最終コマの頭 (23:30) にフォールバックする。網掛けは
   * 「この先」が無いので出さない (下の `hasPostGcRange`)。
   */
  const isAllGateClosed = gcKoma >= KOMA_COUNT
  const gcTime = useMemo(
    () =>
      isAllGateClosed
        ? komaNumberToTime(KOMA_COUNT)
        : komaNumberToTime(gcKoma + 1),
    [gcKoma, isAllGateClosed],
  )
  const currentTime = useMemo(
    () => komaNumberToTime(Math.max(currentKoma, 1)),
    [currentKoma],
  )
  // 現在時刻縦線・GC 縦線・GC 以降網掛けは「当日の締め切り進行状態」を示すもの
  // であり、過去日 (全コマ確定済み) / 未来日 (全コマ未確定) には意味を持たない
  // (spec doji-bg.md AC-BDB-022a)。判定は gcKoma/currentKoma の値から逆算せず
  // displayDate を直接 JST 基準で判定する — backend `GcKomaForDate`
  // (koma_number.go) は未来日でも JST 23:00 以降は ComputeLockedSlots
  // (gate_close.go) が翌日コマをロック済みとして返すため gcKoma は 0 のままとは
  // 限らない (例: 23:10 なら翌日コマ1 がロック済みで gcKoma=1)。gcKoma の値から
  // 「0 なら未来日」と推測する実装は実際に壊れる (詳細画面
  // `BalanceDemandDetail/DetailChart` と同じ判定・同じ理由)。
  const isTodayDisplay = isTargetDateTodayInJst(toProtoDate(displayDate))
  // 天気帯の「過去」コマ数 (AC-BDB-002: 過去=実績アイコン、未来=予報アイコン)。
  // backend の currentKoma は非当日で 0 なので、過去日は全 48 コマを過去として扱う。
  const pastKomaCount = isTodayDisplay
    ? currentKoma
    : isTargetDateBeforeTodayInJst(toProtoDate(displayDate))
      ? KOMA_COUNT
      : 0
  // 過去=実況・未来=予報の 48 コマ絵文字。天気帯とツールチップの両方がこれを引き、
  // 同じコマに 2 つの天気が並ばないようにする (AC-BDB-010b ※ は実況を予報と別扱い
  // にしている。design `js/bg-data.js` も同じカード内で合成済みの天気を使い回す)。
  const komaWeatherIcons = useMemo(
    () =>
      buildPastActualWeatherIcons(weatherIcons, weatherActual, pastKomaCount),
    [weatherIcons, weatherActual, pastKomaCount],
  )
  // 天気帯は 3 時間ごとの正時コマ (6 コマおき) にのみ絵文字を描画する
  // (旧: 同一値 run の中央コマ。run 長が可変で間隔が不規則になるため #5087 で変更)。
  const weatherDisplayIcons = useMemo(
    () => buildWeatherStripDisplayIcons(komaWeatherIcons),
    [komaWeatherIcons],
  )

  const axisTickStyle = {
    ...chartTypography.axis,
    fill: LightModeSemanticColors.Text.midEmphasis.value,
  }

  // 気温・露点は dashboard RPC 側の供給が未接続のコマ/エリアがある (#2002)。
  // トグルだけで ℃ 右軸を出すと、値の無いエリアで空の軸と ℃ ラベルだけが増えて
  // 需要プロットが狭まるので、1 コマでも値があるかで軸と線を絞る。
  const hasTemperature = komaData.some((k) => k.temperature !== null)
  const hasDewPoint = komaData.some((k) => k.dewPoint !== null)
  const temperatureVisible = seriesVisibility.temperature && hasTemperature
  const dewPointVisible = seriesVisibility.dewPoint && hasDewPoint
  const showRightAxis = temperatureVisible || dewPointVisible
  // 天気帯のガター。右軸の出没でプロット幅が変わるので showRightAxis に追従させる。
  const weatherStripGutters = weatherStripGutterWidths(
    Y_AXIS_WIDTH,
    CHART_MARGIN_RIGHT + (showRightAxis ? RIGHT_AXIS_WIDTH : 0),
  )
  // 電圧階級別は「実績を 3 分割する」表示なので、内訳が 1 コマも無いエリアで
  // 積み上げに切り替えると実績が消えるだけになる。その場合は通常の実績エリアを
  // 維持する (hasVoltageBreakdown の godoc 参照)。
  const dataHasBreakdown = hasVoltageBreakdown(komaData)
  const voltage = {
    extraHigh: seriesVisibility.voltageExtraHigh && dataHasBreakdown,
    high: seriesVisibility.voltageHigh && dataHasBreakdown,
    low: seriesVisibility.voltageLow && dataHasBreakdown,
  }
  // 1 階級でも描いていれば「内訳表示中」= 実績値は塗りを帯に譲り輪郭線になる
  // (AC-BDB-026)。全階級 OFF なら通常の実績エリアへ戻す。
  const showVoltageBreakdown = voltage.extraHigh || voltage.high || voltage.low
  // 帯の境界線 (design js/charts.js voltArea)。最上段は自色の輪郭、それ以外は
  // 地色で 1px 抜く。最上段は描かれる階級によって変わるので固定にしない。
  const voltageTop = topVoltageClass(komaData, voltage)

  // ツールチップ (AC-BDB-010) は詳細画面と同じ共有部品で描く。一覧のトグル状態
  // (SeriesVisibility) を部品の語彙へ写し、電圧階級別だけは積み上げの実効値を
  // 渡す — 生値のままだと、内訳が総量と整合せずチャートが積み上げを抑止している
  // コマでもツールチップだけが階級別の値を出してしまう。
  const tooltipVisibility = useMemo<DemandTooltipVisibility>(
    () => ({
      demandPlan: seriesVisibility.demandPlan,
      demandActual: seriesVisibility.demandActual,
      actualCorrected: seriesVisibility.demandActualCorrected,
      voltageExtraHigh: voltage.extraHigh,
      voltageHigh: voltage.high,
      voltageLow: voltage.low,
      weatherStrip: seriesVisibility.weather,
      temperature: seriesVisibility.temperature,
      precipitation: seriesVisibility.precipitation,
      procurementNet: seriesVisibility.procurementNet,
      dewPoint: seriesVisibility.dewPoint,
    }),
    [seriesVisibility, voltage.extraHigh, voltage.high, voltage.low],
  )
  const { chartRef, ...tooltipProps } = useDemandTooltipProps({
    visibility: tooltipVisibility,
    weatherIcons: komaWeatherIcons,
    temperatureForecasts,
    gcKoma,
    currentKoma,
  })

  // AC-BDB-038 / AC-BDB-039 (#5527): 実績が 1 コマも無い時に、 計画値の有無で
  // 表記を出し分ける。 実績の有無は komaData の実績値から判定し、 `summary` (ピーク)
  // では判定しない: 低圧未着のコマはピーク候補から外れるので、 値のあるコマが
  // すべて低圧未着だと実績があっても summary が null になる。
  const hasPlan = komaData.some((k) => k.demandPlan !== null)
  const awaitingRanges = useMemo(() => awaitingKomaRanges(komaData), [komaData])
  const hatchId = useAwaitingVoltageHatchId()
  const hasActual = komaData.some((k) => k.demandActual !== null)

  return (
    <Flex
      flexDirection="column"
      // ツールチップは body 直下へ portal するのでカードの overflow には掛からない
      // (掛かっていた頃の見切れが #4717 の妥協の理由だった)。visible の明示は、
      // チャート本体の収まりを grid item の高さ (react-grid-layout。既定 h=4 行
      // ≒ 298px、リサイズ下限 h=3 行 ≒ 222px が #4717 の 220px 下限を上回る)
      // 側に委ねている印として残す。
      overflow="visible"
      minHeight={0}
      // ⚠ この地色を変える時は電圧階級の帯の区切り線
      // (`BALANCE_VOLTAGE_BAND_SEPARATOR_COLOR.regionCard`) も一緒に変える。
      // 区切り線は「地色で 1px 抜く」表現なので、 片方だけ変えると線が地から浮く。
      //
      // カード本体は白 (`Surface.primary`)、 灰 (`Surface.secondary`) はヘッダのみ
      // (design `css/balance.css:230` `.region-card` / `:233` `.region-card-header`)。
      // 2026-09-07 以前は**逆**で、 カード全面が灰・ヘッダは地色指定なしだったため、
      // ヘッダと本体の分離が枠線 1 本だけになっていた (issue #7064)。
      background="Surface.primary"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="md"
    >
      {/* Header */}
      <Flex
        alignItems="center"
        justifyContent="space-between"
        flexShrink={0}
        // 地色はヘッダのみ灰 (design `.region-card-header`)。 カード本体は白。
        padding="2px 8px 2px 12px"
        // design `css/balance.css:233` = `padding: 2px var(--space-2) 2px var(--space-3)`
        // (上下 2 / 右 8 / 左 12)。 左右が同値の `3px 8px` だと、 左のエリア名が
        // 枠に寄って直下のピル行 (`padding="3px 12px"`) と行頭が揃わない。
        background="Surface.secondary"
        borderBottomWidth="1px"
        borderBottomColor="Border.lowEmphasis"
      >
        <Text
          as="span"
          // design `css/balance.css:234` `.region-card-header .region-title`
          // = `--font-size-md` (14px) / 700。 2026-09-07 以前は sm (12px) だった
          // (issue #7064)。 旧コメントは「design 突合で正値確定後に role 化する」
          // と保留していたが、 その突合がこれ。 role 化 (typography token 化) は
          // md/700 の役割が他画面と共通と確認できてからにするので、 値のみ是正する。
          style={{ fontSize: fontSizes.md.value, fontWeight: 700 }}
        >
          {convertAreaLabel(areaCode)}
        </Text>
        {/* 詳細導線は DS Link に載せる (design `.ecl-text-link` = js/bg-data.js の
            region-card-header 内リンク)。色・下線・inline-flex は DS 既定が
            design 値と一致するので指定しない — 旧 midEmphasis の直指定は
            design の accent primary と別物だったため撤去した。
            D-1: xs(10px)/medium(500) の組に一致する role が無いため typography 値のみ
            raw 維持 (design は sm 相当だがサイズ是正は本 diff の scope 外)。 */}
        {/* design `css/balance.css:236` `.region-card-header .ecl-text-link`
            = `--font-size-sm` (12px) + `white-space: nowrap`。 太さは指定が無いので
            DS Link の既定に委ねる (= 旧 `fontWeight: 500` の直指定を撤去)。
            2026-09-07 以前は xs (10px) / 500 で、 旧コメントも「design は sm=12px だが
            dev は既存の xs=10px を据え置き」 と差分を認識したまま保留していた
            (issue #7064)。 */}
        <Link
          asChild
          style={{ fontSize: fontSizes.sm.value, whiteSpace: 'nowrap' }}
        >
          <RouterLink
            to={`${routePaths.balanceDemandDetail.path}?areaCode=${areaCode}${caseId !== undefined ? `&${WORKFLOW_ID_PARAM}=${caseId}` : ''}`}
          >
            {t('page:balanceDemand.chart.detail')}
            <FaChevronRightRegular
              boxSize={iconSize.guideButton}
              aria-hidden="true"
            />
          </RouterLink>
        </Link>
      </Flex>

      {/* MAX/MIN ピルバッジ (doji-bg.md AC-BDB-002)。design bg.html の
          .region-peaks に合わせ、カードヘッダーと天気帯の間に 1 行で畳む。
          チャート系列トグルには連動させない (design で常時表示)。
          ピークの無いエリア (summary = null。実績が無い、または全コマ低圧未着) は行ごと描かない — 0 を出すと
          未来日のカードが「MAX 0 kWh / 00:00」を実測のように見せる。
          [design差異] design はこの場合「実績なし（予想値のみ）」の 1 行を残すが、
          本実装は行ごと落とす (呼び出し元の確定判断 / 詳細 SummaryBar の
          「0 を出さない」#4494 と同じ扱い)。 */}
      {/* 実績が 1 コマも無い時は、 ピル行の代わりに理由を 1 行で出す
          (AC-BDB-038 / AC-BDB-039、 #5527)。 design (`bg.html`) も同じ位置に
          「実績なし（予想値のみ）」 の 1 行を置いており、 行ごと落としていた
          **既存の design 差異はここで解消**する。 文言は spec 側の確定値
          (2026-08-18 Slack 合意) に合わせる。 */}
      {/* [デザイナー指摘 2026-09-29] 実績も計画値も無い時の 1 行はここから外し、
          **空のチャート中央**へ移した (下の overlay)。 チャートが枠だけになるのは
          この分岐だけで、 目を引くのは空の作画領域そのものなのに、 理由はカード上部の
          小さな caption にあって視線が合っていなかった。
          計画値がある時 (= 系列は描かれる) はチャートが空にならないので、 帯のままが正しい。 */}
      {!hasActual && hasPlan && (
        <Flex
          alignItems="baseline"
          flexShrink={0}
          padding="3px 12px"
          borderBottomWidth="1px"
          borderBottomColor="Border.lowEmphasis"
        >
          <Text textStyle="caption" color="Text.midEmphasis">
            {t('page:balanceDemand.chart.noActualWithPlan')}
          </Text>
        </Flex>
      )}

      {summary !== null && (
        <Flex
          alignItems="baseline"
          flexWrap="wrap"
          flexShrink={0}
          gap="2px 12px"
          padding="3px 12px"
          borderBottomWidth="1px"
          borderBottomColor="Border.lowEmphasis"
        >
          <DemandPeakPill
            kind="max"
            label={t('page:balanceDemand.peak.max')}
            time={summary.maxTime}
            unit={t('page:balanceDemand.peak.kwh')}
            value={summary.maxActual}
            weather={komaWeatherAt(weatherActual, summary.maxKoma)}
          />
          <DemandPeakPill
            kind="min"
            label={t('page:balanceDemand.peak.min')}
            time={summary.minTime}
            unit={t('page:balanceDemand.peak.kwh')}
            value={summary.minActual}
            weather={komaWeatherAt(weatherActual, summary.minKoma)}
          />
        </Flex>
      )}

      {/* Weather strip: plot 領域 (Y 軸の右〜右余白の左) を 48 等分し (doji-bg §48 コマ
          準拠)、3 時間ごとの正時コマ (6 コマおき = 00:00/03:00/…/21:00) にアイコンを
          等間隔で置く (= X 軸の時刻ラベルと同じ粒度、カード幅に追従。#5087 で
          weather-icon-unify-48koma 設計書 AC-07/09 の run 中央配置から変更)。
          左右ガターは半コマぶん削ってセル中央を X 軸のコマ位置に載せる
          (`weatherStripGutterWidths` の JSDoc 参照。#5087)。 */}
      {seriesVisibility.weather && (
        <Flex
          flexShrink={0}
          minHeight="20px"
          padding="2px 0"
          borderBottomWidth="1px"
          borderBottomColor="Border.lowEmphasis"
        >
          <Box flexShrink={0} width={weatherStripGutters.left} />
          {weatherDisplayIcons.map((icon, i) => (
            <Flex key={i} justifyContent="center" flex={1} minWidth={0}>
              <Text
                as="span"
                opacity={i < currentKoma ? 0.4 : 1}
                // D-1: 13px は design スケール外 (xs=10/sm=12/md=14...) の既存値なので
                // fontSize prop 化不可 (token 完全一致以外維持)。typography 値のみ raw 維持。
                style={{ fontSize: '13px', lineHeight: '18px' }}
              >
                {icon}
              </Text>
            </Flex>
          ))}
          <Box
            flexShrink={0}
            width={weatherStripGutters.right}
            marginLeft={weatherStripGutters.rightPull}
          />
        </Flex>
      )}

      {/* Axis labels */}
      <Flex
        justifyContent="space-between"
        flexShrink={0}
        padding="1px 4px 0 0"
        color="Text.midEmphasis"
        // D-1: xs(10px)/semibold(600) の組は TYPOGRAPHY_ROLES に完全一致する role が
        // 無いため typography 値のみ raw 維持。role 化は design 突合後に判断する。
        style={{
          fontSize: fontSizes.xs.value,
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        <span>{t('page:balanceDemand.chart.kwh')}</span>
        {showRightAxis && <span>{t('page:balanceDemand.chart.celsius')}</span>}
      </Flex>

      {/* Chart */}
      {/* minHeight はカード (grid minmax 198px) に header/weather/軸ラベルを載せた残りに
          チャートが収まる floor 値。160px だとカード高を超え X 軸が下端で見切れていた。 */}
      {/*
       * css で recharts の目盛テキストを固定サイズに上書きする: Chakra preflight の
       * `"*": { font: "inherit" } ` が全要素に効き、Recharts の `tick={{ fontSize }}`
       * (= SVG の presentation 属性 `font-size`) が body 継承値 (14px) に負けて効かない
       * (Supply48DataChart.tsx と同じ罠。値の出所は変えず `chartTypography.axis` /
       * ローカルの `axisTickStyle` のままにし、効かせ方だけ css 経由に変える)。
       * 右軸 (気温/露点、10px) だけ他軸 (X軸・左Y軸、11px) とサイズが異なるため、
       * 右軸の YAxis にだけ `className="chart-axis-right"` を明示して selector を
       * 分ける (Recharts は yAxisId 単位で class を分けないため、既定 class
       * `recharts-yAxis` だけでは左右を区別できない — 実 DOM で実測確認済み)。
       */}
      <Box
        ref={chartRef}
        /* チャート作画領域の識別子。 空状態の文字がこの中に入ることを固定するために置く
           (Recharts の `.recharts-responsive-container` に依存すると、 ラッパーや class が
           変わっただけで配置は正しいまま CI が落ちる)。 同ファイルの weather-strip-gutter-* と同じ扱い。 */
        position="relative"
        /* [#9150 デザイナー指摘 2026-09-29] データが 1 コマも無いエリアではチャートを
           描かず、 この Box の中央に理由の文字だけを出す。 `flex` / `minHeight` は
           データの有無で変えない — カードはエリアごとのグリッドなので、 高さを畳むと
           1 枚だけ高さが変わって他のカードの位置がずれる。 */
        flex={1}
        minHeight="100px"
        css={{
          '& .recharts-text': {
            fontSize: `${String(chartTypography.axis.fontSize)}px`,
          },
          [`& .${RIGHT_AXIS_TICK_CLASS}`]: {
            fontSize: `${String(RIGHT_AXIS_TICK_FONT_SIZE)}px`,
          },
          // 複合セレクタで `.recharts-text` (11px) に確実に勝たせる。理由は
          // `CURRENT_LABEL_CLASS` の docstring。
          [`& .recharts-label.${CURRENT_LABEL_CLASS}`]: {
            fontSize: `${String(CURRENT_LABEL_FONT_SIZE)}px`,
          },
          /* 参照線ラベルの太字。 SVG の `font-weight` 属性は Chakra preflight
             (`* { font: inherit }`) に負けるため、 `label={{ fontWeight: 700 }}`
             では **属性が 700 でも computed は `normal`** になる (2026-09-07
             レビュー指摘。 fontSize と同じ罠を隣の prop で踏んでいた)。
             軸目盛りも `.recharts-text` を持つので、 太字はラベルだけに効かせる。 */
          '& .recharts-label': { fontWeight: 700 },
        }}
        data-testid="region-chart-area"
      >
        {hasActual || hasPlan ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              margin={{
                top: 18,
                right: CHART_MARGIN_RIGHT,
                left: 0,
                bottom: 2,
              }}
              // satisfies: 行型が共有ツールチップの契約を満たすことを compile time
              // で固定する (Recharts の payload は型情報を運ばないため、列の改名や
              // null 化がここで止まらないと無言の undefined 表示になる)。
              data={komaData satisfies DemandTooltipKoma[]}
            >
              <CartesianGrid
                stroke={LightModeSemanticColors.Border.lowEmphasis.value}
              />
              <XAxis
                dataKey="time"
                interval={0}
                // point スケールで明示する (詳細画面の DetailChart / WeatherChart と同じ)。
                // 既定 (auto) は降水量 Bar の有無で band に切り替わり、全系列とグリッドが
                // 半コマぶん右へずれて 00:00 の手前に隙間ができる。
                scale="point"
                tick={axisTickStyle}
                tickFormatter={formatChartTick}
              />
              <YAxis
                // width は実データの需要 (7 桁 kWh: 東京 ~3,200,000 等) の目盛りラベルが
                // カード左端で見切れないための実測値。margin.left も 0 に戻している
                // (負値だとラベルが SVG 左外にはみ出しクリップされる)。
                width={Y_AXIS_WIDTH}
                tick={axisTickStyle}
                tickFormatter={formatKwh}
                yAxisId="left"
              />
              {showRightAxis ? (
                <YAxis
                  width={RIGHT_AXIS_WIDTH}
                  // 気温・露点の目盛りは **実データ幅に合わせる** (#5893)。 固定
                  // `[-5, 35]` だと、 実際には 22〜28℃ しか動かない日でも 40℃ ぶんの
                  // 目盛りに押し込まれて変化が読めなかった (2026-08-18 デザイナー指摘)。
                  // 気温と露点はどちらも ℃ なので軸は 1 本のまま (別軸に分けると縦位置
                  // が揃わず、 同じ温度どうしの比較ができなくなる)。
                  // 対応する design 関数は需要+気温 1 枚の `renderBgChart` で、そちらは
                  // 今も右軸 `[-5, 35]` 固定。 ここは 2026-08-18 デザイナー指摘への
                  // **意図的な乖離**。 同じ domain 式の先例は分割チャート
                  // `renderBgTempDewChart` の左軸 (`js/charts.js`)。
                  // allowDecimals=false: auto domain だと 14.5 のような小数目盛りになり、
                  // カード右端で見切れる。整数に揃える。幅 40 は詳細チャートと同じ。
                  allowDecimals={false}
                  domain={['dataMin - 2', 'dataMax + 2']}
                  orientation="right"
                  tick={<RightAxisTick />}
                  yAxisId="right"
                />
              ) : (
                <YAxis hide yAxisId="right" />
              )}
              {/* 降水量専用 Y 軸 (hidden / 0-30mm)。 design `js/charts.js:1058-1060`
                (renderBgChart) が
                `ce(R.YAxis, {yAxisId:'rightPrecip', orientation:'right', domain:[0,30], hide:true})`
                を持ち、 同 :1058 に「気温軸と混在しないよう独立スケールにする」と明記。
                気温軸に相乗りすると mm 値が ℃ 目盛で読まれる。 **こちらは固定 `[0, 30]`
                のまま**にする — 気温軸と違い、 auto domain にすると気温・露点が無い
                エリアで数 mm の雨でも bar が全高まで伸びて豪雨に見える。 */}
              <YAxis
                domain={[0, 30]}
                hide
                orientation="right"
                yAxisId="rightPrecip"
              />
              <Tooltip {...tooltipProps} />

              {/* Areas */}
              {/* 電圧階級別の積み上げ (AC-BDB-026)。階級ごとに個別 ON/OFF でき、
                OFF の階級は stackId を共有する子ごと外れるので積み上げが詰まる。
                実績の輪郭線より先に置く — 後に置くと帯の塗りに覆われる。 */}
              {voltage.low && (
                <Area
                  fill={BALANCE_VOLTAGE_CLASS_COLORS.voltageLow}
                  stroke={
                    voltageTop === 'low'
                      ? BALANCE_VOLTAGE_CLASS_COLORS.voltageLow
                      : BALANCE_VOLTAGE_BAND_SEPARATOR_COLOR.regionCard
                  }
                  dataKey="demandLow"
                  dot={false}
                  fillOpacity={0.85}
                  isAnimationActive={false}
                  stackId="volt"
                  strokeWidth={voltageTop === 'low' ? 1.5 : 1}
                  type="stepAfter"
                  yAxisId="left"
                />
              )}
              {voltage.high && (
                <Area
                  fill={BALANCE_VOLTAGE_CLASS_COLORS.voltageHigh}
                  stroke={
                    voltageTop === 'high'
                      ? BALANCE_VOLTAGE_CLASS_COLORS.voltageHigh
                      : BALANCE_VOLTAGE_BAND_SEPARATOR_COLOR.regionCard
                  }
                  dataKey="demandHigh"
                  dot={false}
                  fillOpacity={0.85}
                  isAnimationActive={false}
                  stackId="volt"
                  strokeWidth={voltageTop === 'high' ? 1.5 : 1}
                  type="stepAfter"
                  yAxisId="left"
                />
              )}
              {voltage.extraHigh && (
                <Area
                  fill={BALANCE_VOLTAGE_CLASS_COLORS.voltageExtraHigh}
                  stroke={
                    voltageTop === 'extraHigh'
                      ? BALANCE_VOLTAGE_CLASS_COLORS.voltageExtraHigh
                      : BALANCE_VOLTAGE_BAND_SEPARATOR_COLOR.regionCard
                  }
                  dataKey="demandExtraHigh"
                  dot={false}
                  fillOpacity={0.85}
                  isAnimationActive={false}
                  stackId="volt"
                  strokeWidth={voltageTop === 'extraHigh' ? 1.5 : 1}
                  type="stepAfter"
                  yAxisId="left"
                />
              )}

              {/* 実績値: design charts.js 準拠 (chart-1 / fillOpacity 0.4 /
                strokeWidth 1.5)。詳細画面と同じ理由で輪郭線を持たせる。
                内訳表示中は塗りを帯に譲り、合計の輪郭線として残す (AC-BDB-026) —
                階級を個別に消すと積み上げの天井が総量でなくなるため、総量はこの線で
                読む。トグル OFF の時は輪郭線も出さない (AC-BDB-031 が優先)。 */}
              {seriesVisibility.demandActual && (
                <Area
                  fill={BALANCE_DEMAND_SERIES_COLORS.demandActual}
                  stroke={BALANCE_DEMAND_SERIES_COLORS.demandActual}
                  dataKey="demandActual"
                  dot={false}
                  fillOpacity={showVoltageBreakdown ? 0 : 0.4}
                  isAnimationActive={false}
                  strokeWidth={showVoltageBreakdown ? 2 : 1.5}
                  type="stepAfter"
                  yAxisId="left"
                />
              )}

              {/* 実績値(補完): 補完済みコマの実績値そのもの。詳細画面
                (DetailChart) と同じ design 準拠の色・輪郭線を使い、同じ理由で
                実績値の後 (= 上) に描く。詳細側のコメント参照。
                内訳表示中の扱いは実績値と同じ (塗りを落として輪郭線で残す)。 */}
              {seriesVisibility.demandActualCorrected && (
                <Area
                  fill={BALANCE_DEMAND_SERIES_COLORS.demandActualCorrected}
                  stroke={BALANCE_DEMAND_SERIES_COLORS.demandActualCorrected}
                  dataKey="demandActualCorrected"
                  dot={false}
                  fillOpacity={showVoltageBreakdown ? 0 : 0.3}
                  isAnimationActive={false}
                  strokeWidth={showVoltageBreakdown ? 2 : 1.5}
                  type="stepAfter"
                  yAxisId="left"
                />
              )}

              {/* Precipitation bar */}
              {seriesVisibility.precipitation && (
                <Bar
                  fill={BALANCE_DEMAND_SERIES_COLORS.precipitation}
                  dataKey="precipitation"
                  fillOpacity={0.5}
                  isAnimationActive={false}
                  yAxisId="rightPrecip"
                />
              )}

              {/* Lines */}
              {/* doji-bg.md §前提・依存「受領済み値が一度も無い場合は非表示」 */}
              {seriesVisibility.demandPlan && hasPlan && (
                <Line
                  stroke={BALANCE_DEMAND_SERIES_COLORS.demandPlan}
                  dataKey="demandPlan"
                  dot={false}
                  isAnimationActive={false}
                  strokeWidth={2.5}
                  type="stepAfter"
                  yAxisId="left"
                />
              )}
              {seriesVisibility.procurementNet && (
                <Line
                  stroke={BALANCE_DEMAND_SERIES_COLORS.procurementNet}
                  dataKey="procurementNet"
                  dot={false}
                  isAnimationActive={false}
                  strokeDasharray="6 3"
                  strokeWidth={1.5}
                  type="stepAfter"
                  yAxisId="left"
                />
              )}
              {temperatureVisible && (
                <Line
                  stroke={LightModeSemanticColors.Text.negativeMid.value}
                  dataKey="temperature"
                  dot={false}
                  isAnimationActive={false}
                  strokeWidth={2}
                  type="linear"
                  yAxisId="right"
                />
              )}
              {dewPointVisible && (
                <Line
                  stroke={LightModeSemanticColors.Brand.teal.value}
                  dataKey="dewPoint"
                  dot={false}
                  isAnimationActive={false}
                  strokeWidth={2}
                  type="linear"
                  yAxisId="right"
                />
              )}

              {/* 低圧未着のコマ。コマの属性なので系列トグルに従わず、
                表示日の当日判定にも従わない (過去日でも低圧が未着なら印を付ける)。 */}
              {awaitingRanges.length > 0 && (
                <defs>
                  <AwaitingVoltageHatchPattern patternId={hatchId} />
                </defs>
              )}
              {awaitingRanges.map((range) => (
                <ReferenceArea
                  className={BALANCE_AWAITING_VOLTAGE_HATCH.className}
                  key={range.x1}
                  fill={`url(#${hatchId})`}
                  stroke="none"
                  fillOpacity={1}
                  x1={range.x1}
                  x2={range.x2}
                  // 需要の系列が左軸に 1 つも無いと左軸が domain を持たず網掛けも消える
                  // (GC 以降の網掛けと同じ)。domain 固定の非表示軸は、載る系列が無いと
                  // Recharts が scale を作らないので代わりにならない (実測)。
                  yAxisId="left"
                />
              ))}

              {/* Reference lines: 非当日 (過去日・未来日) は描画しない
                (spec AC-BDB-022a、判定根拠は isTodayDisplay 算出部のコメント参照)。 */}
              {isTodayDisplay && (
                <>
                  <ReferenceLine
                    stroke={LightModeSemanticColors.Surface.accentPrimary.value}
                    /* 「現在」 ラベル。 `dy: 13` で 1 行ぶん下げるのは design の判断で、
                     理由も残っている (`js/charts.js:1269-1270`):
                     「GC 線が現在より約 1 時間先に立つようになり、 insideTopLeft の
                      「現在」 と insideTopRight の「GC HH:MM」 が同じ高さで重なって
                      読めなくなったため」。 文字サイズは class 側で当てる
                     (`CURRENT_LABEL_CLASS` の docstring 参照)。 */
                    label={{
                      className: CURRENT_LABEL_CLASS_NAMES,
                      value: t('page:balanceDemand.chart.current'),
                      position: 'insideTopLeft',
                      fill: LightModeSemanticColors.Surface.accentPrimary.value,
                      dy: 13,
                    }}
                    strokeWidth={1.5}
                    x={currentTime}
                    yAxisId="left"
                  />
                  {/* GC 以降の網掛け。全コマ GC 済みのときは「この先」が無いので
                    出さない (design `js/charts.js:867` も `gcKoma < 48` の時だけ
                    `gcAreaStart` を作る)。 */}
                  {!isAllGateClosed && (
                    <ReferenceArea
                      fill={LightModeSemanticColors.Text.disabled.value}
                      stroke="none"
                      fillOpacity={0.08}
                      x1={gcTime}
                      x2="23:30"
                      yAxisId="left"
                    />
                  )}
                  <ReferenceLine
                    /* GC の赤は **`Surface.negativeHigh`** (#d80004)。 `Text.negativeHigh` (#870103) は
                     暗いマルーンで、 design の GC 色 (`js/charts.js:876` `cssVar('surface-negative-high')`)
                     と別物 (2026-09-03 デザイナー指摘 #7064)。 線・ラベル・バッジで同じ 1 色を使う。 */
                    stroke={LightModeSemanticColors.Surface.negativeHigh.value}
                    /* 「GC HH:MM」 ラベル (design `js/charts.js:1276`)。 11px は
                     コンテナの `.recharts-text` 規則にそのまま乗るので指定しない
                     (指定しても SVG 属性は CSS に負ける)。 */
                    label={{
                      value: `${t('page:balanceDemand.chart.gc')} ${gcTime}`,
                      position: 'insideTopRight',
                      fill: LightModeSemanticColors.Surface.negativeHigh.value,
                    }}
                    strokeDasharray="4 4"
                    strokeWidth={2}
                    x={gcTime}
                    yAxisId="left"
                  />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          /* [デザイナー指摘 2026-09-29] 実績も計画値も無いエリア。 AC-BDB-039 で系列を
             描かないため、 チャートを出すと枠 (軸・グリッド) だけが残る。 空の枠は
             「読み込み中なのか / 壊れているのか / 本当に無いのか」 が読めないうえ、
             見えているのに読むものが無い分だけ目を引くので、 **チャートごと描かず**
             理由の文字だけを作画領域の中央に出す (2026-09-29 の追加指摘。 当初は枠を
             残して文字を重ねていた)。
             Box の `flex` / `minHeight` は据え置き = カードの高さは変わらないので、
             エリアごとのグリッドで 1 枚だけ高さがずれることはない。
             文言は従来カード上部の帯に出していたものと同一キー (二重に出さない)。 */
          <Flex alignItems="center" justifyContent="center" height="100%">
            <Text textStyle="body" color="Text.midEmphasis">
              {t('page:balanceDemand.chart.noActualNoPlan')}
            </Text>
          </Flex>
        )}
      </Box>
    </Flex>
  )
}
